import {
  IconExpandItemsLine,
  Menu,
  IconExternalLinkLine,
  IconNoLine,
  IconButton,
  IconSettingsLine,
  IconCommentLine,
  IconEyeLine,
  Mask,
  Spinner,
} from '@instructure/ui';
import { Flex, Text, View, Badge, Tooltip } from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import PropTypes from 'prop-types';
import { SessionViewer } from '@shared/components/proctoring/SessionViewer';

const apiUrl = import.meta.env.VITE_API_URL;
const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL;

import Card from '../Common/Card';
import { useEffect, useState, useMemo, useCallback } from 'react';

const SessionCard = ({ sessionSid, data }) => {
  const {
    setTransferSessionSid,
    setEndSessionSid,
    focusedSession,
    focusedSessionSid,
    addSessionMediaConnectionBundle,
  } = useSessionStore();
  const { openDrawer, allowSounds } = useAppStore();

  const focusedSessionLocation = useSessionStore(
    (state) => state.focusedSession?.location
  );

  const user = useAppStore((state) => state.user);
  const [allowFocusOption, setAllowFocusOption] = useState(true);
  const [unReadMessageCount, setUnreadMessageCount] = useState(0);
  const [flagCount, setFlagCount] = useState(0);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');

  const sessionData = useSessionStore.getState().getSessionData(sessionSid);



  useEffect(() => {
    if (!sessionLoaded) {
      setLoaderMessage('Loading...');
      setShowLoader(true);
    }
  }, []);

  useEffect(() => {
    // If the focused session is in review, do not allow the user to focus the session...
    if (focusedSession?.location === 'review') {
      setAllowFocusOption(false);
    } else {
      setAllowFocusOption(true);
    }
  }, [focusedSessionLocation]);

  const handleUnreadMessageCountChange = (count) => {
    // if the count is greater than 0 and we are not focused on someone, play a sound...
    if (count > 0 && allowSounds) {
      const audio = new Audio('/assets/sounds/message-received.mp3');
      audio.play();
    }
    setUnreadMessageCount(count);
  };

  const handleFlagCountChange = (count) => {
    setFlagCount(count);
  };

  const handleSessionLoaded = useCallback(
    (options) => {
      setSessionLoaded(true);
      setShowLoader(false);
    },
    [sessionSid]
  );

  const handleOnConnected = (mediaConnectionBundle) => {
    console.log(mediaConnectionBundle);
    addSessionMediaConnectionBundle(mediaConnectionBundle.roomId, mediaConnectionBundle);
    setShowLoader(false);
  };

  const handleDisconnected = () => {
    setLoaderMessage('Attempting Reconnection');
    setShowLoader(true);
  };



  const sessionProps = useMemo(
    () => ({
      sessionSid,
      installSid: sessionData?.installSid,
      examSid: sessionData?.examSid,
      apiUrl,
      roomId: sessionSid,
      //spToken: user.token,
      spToken: "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpZCI6IjMyMzY1NTAwLTcwNTQtMTFlZi1iZDdhLTViODA3NmQyNjIwMyIsImNyZWF0ZV90aW1lIjoxNzI2MDY5MjM5Mzc2LCJ0aW1lb3V0IjowfQ.heak3mv9ot-n2QYArb8R2WOKQdmWrTLu-CSxs4X-1cU",
      identity: user.identity,
      websocketUrl,
      onSessionLoaded: handleSessionLoaded,
      onUnreadMessageCount: handleUnreadMessageCountChange,
      onFlaggingChange: handleFlagCountChange,
      onConnected: handleOnConnected,
      onDisconnected: handleDisconnected,
      type:"proctor",
      enabledComponents: {
        microphone: false,
        camera: false,
        chat: false,
        screenShare: false,
        settings: false,
        leave: false,
      },
      video:{
        sources: ['live', 'recorded'],
        default: 'live',
      },
    }),
    [
      sessionSid,
      sessionData?.installSid,
      sessionData?.examSid,
      apiUrl,
      websocketUrl,
      user.token,
      user.identity,
      handleSessionLoaded,
      handleUnreadMessageCountChange,
      handleFlagCountChange,
      handleDisconnected,
    ]
  );

  return (
    <>
      <Card>
        <Card.Header
          background={focusedSessionSid !== sessionSid ? 'primary' : 'danger'}
        >
          <Card.Title>
            {focusedSessionSid !== sessionSid ? (
              <>
                <Text weight="bold">
                  {data?.firstName} {data?.lastName}
                </Text>

                <View margin="none none none xx-small" padding="none">
                  <Tooltip
                    color="primary"
                    renderTip="Erik Nermo is focused"
                    placement="bottom"
                  >
                    <IconEyeLine />
                  </Tooltip>
                </View>
              </>
            ) : (
              <Text weight="bold">Focused</Text>
            )}
          </Card.Title>

          {/* Only show the actions if you are not currently reviewing the session... */}
          {focusedSessionSid !== sessionSid && (
            <Card.Actions>
              {flagCount > 0 && (
                <Badge
                  standalone
                  variant="danger"
                  pulse={true}
                  count={flagCount}
                  countUntil={10}
                  margin="0 xx-small 0 0"
                />
              )}
              {unReadMessageCount > 0 && (
                <IconButton
                  withBackground={false}
                  withBorder={false}
                  color="danger"
                  size="small"
                  screenReaderLabel="Unread Messages"
                  margin="none"
                  padding="none"
                  onClick={() => openDrawer('focus', sessionSid)}
                >
                  <IconCommentLine />
                </IconButton>
              )}
              <Menu
                placement="bottom end"
                shouldContainFocus
                shouldReturnFocus
                mountNode={() => document.body} // Ensures the menu overlays everything
                trigger={
                  <IconButton
                    withBackground={false}
                    withBorder={false}
                    color="primary"
                    size="small"
                    screenReaderLabel="Session Options"
                    margin="none"
                    padding="none"
                  >
                    <IconSettingsLine />
                  </IconButton>
                }
              >
                <Menu.Item onSelect={() => setTransferSessionSid(sessionSid)}>
                  <Flex alignItems="center">
                    <Flex.Item margin="0 xx-small 0 0">
                      <IconExternalLinkLine size="x-small" />
                    </Flex.Item>
                    <Flex.Item>
                      <Text>Transfer</Text>
                    </Flex.Item>
                  </Flex>
                </Menu.Item>
                <Menu.Separator />
                <Menu.Item onSelect={() => setEndSessionSid(sessionSid)}>
                  <Flex alignItems="center">
                    <Flex.Item margin="0 xx-small 0 0">
                      <IconNoLine size="x-small" />
                    </Flex.Item>
                    <Text>End Session</Text>
                  </Flex>
                </Menu.Item>
              </Menu>
              {allowFocusOption && (
                <IconButton
                  withBackground={false}
                  withBorder={false}
                  color="primary"
                  size="small"
                  screenReaderLabel="Expand To Focus Mode"
                  margin="none"
                  padding="none"
                  onClick={() => openDrawer('focus', sessionSid)}
                >
                  <View
                    display="inline-block"
                    style={{ transform: 'rotate(45deg)' }}
                  >
                    <IconExpandItemsLine />
                  </View>
                </IconButton>
              )}
            </Card.Actions>
          )}
        </Card.Header>
        <Card.Progress
          valueMax={100}
          valueNow={Math.floor(Math.random() * 100) + 1}
          themeOverride={{
            xSmallHeight: '0.2em',
          }}
        />
        <Card.Body>
          <View
            as="div"
            id="SessionBody"
            height="100%"
            style={{ position: 'relative' }}
          >
            <SessionViewer
              key={`react-session-${sessionSid}`}
              {...sessionProps}
            />

            {showLoader && (
              <Mask
                placement="center"
                themeOverride={{
                  zIndex: 99999,
                  background: 'rgba(0, 0, 0, 0.60)',
                }}
              >
                <Spinner renderTitle="Loading" size="small" />
                <View as="div">
                  <Text size="large" color="secondary-inverse">
                    {loaderMessage}
                  </Text>
                </View>
              </Mask>
            )}
          </View>
        </Card.Body>
      </Card>
    </>
  );
};

SessionCard.propTypes = {
  sessionSid: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
};

export default SessionCard;
